import { useCallback, useEffect, useState } from 'react'
import type { BloggerListQuery, BloggerStage } from '@ayub/shared'
import { cn } from '@/lib/utils'
import { BloggerKanbanView } from './BloggerKanbanView'
import { BloggerListView } from './BloggerListView'
import { BloggerStageChips } from './BloggerStageChips'
import { BloggerToolbar } from './BloggerToolbar'
import type { BloggerView } from './BloggerToolbar'
import { STAGE_ALL, useBloggerList, useBloggerStageCounts, useDebouncedValue } from './hooks'
import type { StageFilter } from './hooks'

type SortKey = NonNullable<BloggerListQuery['sort']>
type SortOrder = NonNullable<BloggerListQuery['order']>

export interface BloggersPageProps {
  /** Qator/kartochka bosilganda — bloger kartochkasini ochish integratsiyaga qoldiriladi. */
  onSelectBlogger: (id: number) => void
  /**
   * Kanban'da kartochka boshqa ustunga sudralganda — bosqichni o'zgartirish SO'ROVI.
   * `true` qaytarsa qabul qilingan (server chaqirilgan va muvaffaqiyatli), `false` bo'lsa
   * kartochka avtomatik eski joyiga qaytadi. Batafsil: `BloggerKanbanView` izohiga qarang.
   */
  onStageChange: (
    bloggerId: number,
    newStage: BloggerStage,
    previousStage: BloggerStage,
  ) => Promise<boolean> | boolean
}

/**
 * Sahifa 3 — Blogerlar (`docs/03-app-flow.md`). Standart ko'rinish LIST; Kanban'ga
 * o'tish mumkin. Qidiruv, bosqich filtri va saralash holati ikkala ko'rinish orasida
 * saqlanadi — faqat `view` o'zgaradi, qolgan state shu komponentda umumiy.
 */
export function BloggersPage({ onSelectBlogger, onStageChange }: BloggersPageProps) {
  const [view, setView] = useState<BloggerView>('list')
  const [searchInput, setSearchInput] = useState('')
  const [stageFilter, setStageFilter] = useState<StageFilter>(STAGE_ALL)
  const [sort, setSort] = useState<SortKey>('createdAt')
  const [order, setOrder] = useState<SortOrder>('desc')
  const [page, setPage] = useState(1)

  const debouncedSearch = useDebouncedValue(searchInput, 350)

  // Qidiruv o'zgarganda (haqiqatan debounce'dan o'tgach) sahifani boshiga qaytaramiz —
  // aks holda 7-sahifada turib qidirsangiz "hech narsa yo'q" bo'lib ko'rinishi mumkin.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  const { counts, isLoading: countsLoading } = useBloggerStageCounts()

  const list = useBloggerList({ stage: stageFilter, q: debouncedSearch, page, sort, order })

  const handleStageFilterChange = useCallback((next: StageFilter) => {
    setStageFilter(next)
    setPage(1)
  }, [])

  const handleSortChange = useCallback((nextSort: SortKey, nextOrder: SortOrder) => {
    setSort(nextSort)
    setOrder(nextOrder)
    setPage(1)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    setStageFilter(STAGE_ALL)
    setPage(1)
  }, [])

  const hasActiveFilters = debouncedSearch.length > 0 || stageFilter !== STAGE_ALL

  return (
    // Diqqat: tashqi konteyner ATAYLAB kenglikka cheklanmagan. Faqat List (jadval)
    // ko'rinishi o'zi `max-w-5xl` bilan cheklanadi (o'qish uchun qulay) — Kanban esa
    // to'liq eninigа yoyiladi, aks holda 4 ustun (300px + oraliqlar ≈ 1240px) katta
    // ekranda ham ichki gorizontal skrollga majburlanadi, docs/04-ui-ux.md talabiga zid:
    // "≥1024px: ... kanban to'liq ko'rinadi".
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Blogerlar</h1>
      </header>

      <div className={cn(view === 'list' && 'mx-auto w-full max-w-5xl')}>
        <BloggerToolbar
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          view={view}
          onViewChange={setView}
        />
      </div>

      {view === 'list' && (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
          <BloggerStageChips
            value={stageFilter}
            onChange={handleStageFilterChange}
            counts={counts}
            isLoading={countsLoading}
          />
          <BloggerListView
            bloggers={list.data?.items ?? []}
            total={list.data?.total ?? 0}
            page={page}
            isLoading={list.isLoading}
            isFetching={list.isFetching}
            error={list.error}
            sort={sort}
            order={order}
            searchTerm={debouncedSearch}
            hasActiveFilters={hasActiveFilters}
            onSortChange={handleSortChange}
            onPageChange={setPage}
            onSelectBlogger={onSelectBlogger}
            onRetry={() => void list.refetch()}
            onClearFilters={handleClearFilters}
          />
        </div>
      )}

      {view === 'kanban' && (
        <BloggerKanbanView
          searchTerm={debouncedSearch}
          onSelectBlogger={onSelectBlogger}
          onStageChange={onStageChange}
        />
      )}
    </div>
  )
}
